import { FC, useState } from 'react';
import { TwoFaSectionTitle } from '../../../shared/TwoFaSectionTitle';
import { TwoFaSectionDescription } from '../../../shared/TwoFaSectionDescription';
import { TwoFaSectionButtonsWrapper } from '../../../shared/TwoFaSectionButtonsWrapper';
import { ButtonBase } from '../../../../../ButtonBase/ButtonBase';
import { DeleteTwoFaOnContinue, ICodeResponseType } from '../../utils/types';
import { getAppConfig } from '@bts-web/core-app-config';
import { css } from '@bts-web/utils-style-engine';
import { HyperLinkButtonBase } from '../../../../../HyperLinkButtonBase/HyperLinkButtonBase';
import { DigitCodeInput } from '../../../../../DigitCodeInput';
import { submitResetTwoFaAppCode } from '../../../../../../../auth';
import { useHandleGqlErrorsWithSnackbar } from '../../../../../../utils/errors';
import { TwoFaScreenTranslations } from '../../utils/get2faTranslations';

type TwoFaInputStatus = 'invalid' | 'valid';

const ResetTwoFaMethodAppCode: FC<{
  onContinue: DeleteTwoFaOnContinue;
  dismissLoading?: boolean;
  translations: TwoFaScreenTranslations;
  bindICodeResponse: (response: ICodeResponseType) => void;
}> = ({ onContinue, dismissLoading, bindICodeResponse, translations }) => {
  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const [inputStatus, setInputStatus] = useState<TwoFaInputStatus>('valid');

  const { URLforCustomerSupport } = getAppConfig();

  const [inputCode, setInputCode] = useState('');

  const [localLoading, setLocalLoading] = useState(false);

  const isCodeValid =
    inputCode.length === 6 &&
    inputCode.split('').every((digit) => /^\d$/.test(digit));

  const anyLoading = dismissLoading || localLoading;

  const onSubmitCode = async (code: string) => {
    setLocalLoading(true);

    try {
      const resp = await submitResetTwoFaAppCode(code);

      if (resp.errors || resp.message === 'invalid code') {
        setInputStatus('invalid');

        if (resp.errors) {
          processErrors(resp.errors);
        }

        setLocalLoading(false);

        return;
      } else {
        setLocalLoading(false);

        onContinue();

        bindICodeResponse(resp);
      }
    } catch (_error) {
      setLocalLoading(false);

      setInputStatus('invalid');

      return;
    }
  };

  const onChangeInput = (isFilled: boolean, code: string) => {
    if (code !== inputCode) {
      setInputStatus('valid');
    }

    setInputCode(code);
  };

  return (
    <>
      <TwoFaSectionTitle title={translations.reset2faAppcodeTitle} />
      <TwoFaSectionDescription
        textContent={translations.reset2faAppcodeDescription}
      />
      <div>
        <DigitCodeInput
          onChange={onChangeInput}
          hasError={inputStatus === 'invalid'}
          className={css({
            width: '100%',
            maxWidth: '340px',
            margin: '0 auto',
          })}
        />
        {inputStatus === 'invalid' && (
          <p
            className={css({
              color: 'negative.text_primary',
              textAlign: 'center',
              py: 'extra_small_3',
              fontSize: 'caption.small',
            })}
          >
            {translations.reset2faInvalidCode}
          </p>
        )}
      </div>

      <TwoFaSectionButtonsWrapper>
        {URLforCustomerSupport && (
          <HyperLinkButtonBase
            visual="ghost"
            to={URLforCustomerSupport}
            className={css({
              textDecoration: 'underline',
            })}
          >
            {translations.reset2faOptionHelpdesk}
          </HyperLinkButtonBase>
        )}
        <ButtonBase
          isLoading={anyLoading}
          onClick={() => onSubmitCode(inputCode)}
          visual="primary"
          size="large"
          fullWidth
          disabled={anyLoading ? true : !isCodeValid || inputStatus !== 'valid'}
        >
          {translations.reset2faButton}
        </ButtonBase>
      </TwoFaSectionButtonsWrapper>
    </>
  );
};

export { ResetTwoFaMethodAppCode };
